/**
 * DiagramCanvas.tsx — the drawing surface.
 *
 * Everything in this component is interaction: pan, zoom, hover, selection, the
 * drag ghost, the connect rubber-band. None of it is engineering. When the user
 * finishes a gesture the component reports an INTENT — "place this symbol about
 * here", "connect this port to that one" — and the Engine decides the grid
 * position, the collision-free origin, the port compatibility and the route.
 *
 * The consequence worth stating: the picture on screen after a gesture is not
 * the picture the client predicted. It is the projection the Engine returned.
 * The ghost is feedback, never a commitment.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DiagramRenderProjection, ProjectedPort, ProjectedSymbol } from "../types/contract";
import { instanceTransform, renderPrimitive } from "./primitives";
import { fitToSheet, panBy, toWorld, zoomAt, type Viewport } from "./viewport";
import { gridStepForScale, snapForDisplay } from "./grid";
import { hitTest, type Hit } from "./hitTest";

export interface PlacementIntent {
  defId: string;
  /** Where the user dropped it, world mm. The Engine resolves the real origin. */
  atMm: { x: number; y: number };
}

export interface ConnectIntent {
  from: { symbolId: string; portId: string };
  to: { symbolId: string; portId: string };
}

export interface MoveIntent {
  symbolId: string;
  toMm: { x: number; y: number };
}

export interface DiagramCanvasProps {
  projection: DiagramRenderProjection;
  /** The definition the palette has armed for placement, if any. */
  pendingDefId?: string | undefined;
  selectedId?: string | undefined;
  readOnly?: boolean;
  onSelect?: (hit: Hit) => void;
  onPlace?: (intent: PlacementIntent) => void;
  onConnect?: (intent: ConnectIntent) => void;
  onMove?: (intent: MoveIntent) => void;
}

interface DragState {
  kind: "pan" | "move" | "connect";
  startScreen: { x: number; y: number };
  startViewport: Viewport;
  symbol?: ProjectedSymbol;
  port?: ProjectedPort;
  currentWorld: { x: number; y: number };
}

export function DiagramCanvas(props: DiagramCanvasProps): JSX.Element {
  const { projection, readOnly = false } = props;
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 1200, height: 800 });
  const [viewport, setViewport] = useState<Viewport>(() => fitToSheet(projection.sheet, { width: 1200, height: 800 }));
  const [drag, setDrag] = useState<DragState | undefined>(undefined);
  const [hover, setHover] = useState<Hit>({ kind: "none" });
  const [pointerWorld, setPointerWorld] = useState<{ x: number; y: number } | undefined>(undefined);

  // Refit when the sheet changes — a different diagram, not a redraw of this one.
  const sheetKey = `${projection.diagramId}:${projection.sheet.widthMm}x${projection.sheet.heightMm}`;
  const fittedFor = useRef(sheetKey);
  useEffect(() => {
    if (fittedFor.current === sheetKey) return;
    fittedFor.current = sheetKey;
    setViewport(fitToSheet(projection.sheet, size));
  }, [sheetKey, projection.sheet, size]);

  useEffect(() => {
    const element = surfaceRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(entries => {
      const rect = entries[0]?.contentRect;
      if (rect && rect.width > 0 && rect.height > 0) setSize({ width: rect.width, height: rect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const screenOf = useCallback((event: { clientX: number; clientY: number }): { x: number; y: number } => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    return { x: event.clientX - (rect?.left ?? 0), y: event.clientY - (rect?.top ?? 0) };
  }, []);

  /** A constant-on-screen pick radius: 8 px, whatever the zoom. */
  const toleranceMm = 8 / viewport.scale;

  const onWheel = useCallback((event: React.WheelEvent) => {
    event.preventDefault();
    setViewport(current => zoomAt(current, screenOf(event), event.deltaY < 0 ? 1.12 : 1 / 1.12));
  }, [screenOf]);

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    (event.target as Element).setPointerCapture?.(event.pointerId);
    const screen = screenOf(event);
    const world = toWorld(viewport, screen);
    const hit = hitTest(projection, world, { toleranceMm });

    // Middle button, space-less pan, or empty space: pan. Everything else is
    // an interaction with an object.
    if (event.button === 1 || hit.kind === "none") {
      setViewport(current => current);
      setDrag({ kind: "pan", startScreen: screen, startViewport: viewport, currentWorld: world });
      return;
    }

    props.onSelect?.(hit);
    if (readOnly) return;

    if (hit.kind === "port") {
      setDrag({ kind: "connect", startScreen: screen, startViewport: viewport, symbol: hit.symbol, port: hit.port, currentWorld: world });
      return;
    }
    if (hit.kind === "symbol") {
      setDrag({ kind: "move", startScreen: screen, startViewport: viewport, symbol: hit.symbol, currentWorld: world });
    }
  }, [projection, props, readOnly, screenOf, toleranceMm, viewport]);

  const onPointerMove = useCallback((event: React.PointerEvent) => {
    const screen = screenOf(event);
    const world = toWorld(viewport, screen);
    setPointerWorld(world);

    if (!drag) {
      setHover(hitTest(projection, world, { toleranceMm }));
      return;
    }
    if (drag.kind === "pan") {
      setViewport(panBy(drag.startViewport, { x: screen.x - drag.startScreen.x, y: screen.y - drag.startScreen.y }));
      return;
    }
    setDrag({ ...drag, currentWorld: world });
    if (drag.kind === "connect") setHover(hitTest(projection, world, { toleranceMm }));
  }, [drag, projection, screenOf, toleranceMm, viewport]);

  const onPointerUp = useCallback((event: React.PointerEvent) => {
    const world = toWorld(viewport, screenOf(event));

    if (drag?.kind === "connect" && drag.symbol && drag.port) {
      const target = hitTest(projection, world, { toleranceMm });
      // Only a port ends a connect gesture. Dropping on a body would mean the
      // client choosing which port to use — a resolution the Engine owns.
      if (target.kind === "port" && !(target.symbol.id === drag.symbol.id && target.port.id === drag.port.id)) {
        props.onConnect?.({
          from: { symbolId: drag.symbol.id, portId: drag.port.id },
          to: { symbolId: target.symbol.id, portId: target.port.id },
        });
      }
    } else if (drag?.kind === "move" && drag.symbol) {
      const moved = Math.hypot(world.x - toWorld(viewport, drag.startScreen).x, world.y - toWorld(viewport, drag.startScreen).y);
      // A click is not a move. Below a grid step, the gesture was a selection.
      if (moved >= 2.5) props.onMove?.({ symbolId: drag.symbol.id, toMm: snapForDisplay(world) });
    }

    // Placement is handled by `onClick`, which fires after every completed
    // pointer sequence. Doing it here as well would place twice.
    setDrag(undefined);
  }, [drag, projection, props, screenOf, toleranceMm, viewport]);

  const onClick = useCallback((event: React.MouseEvent) => {
    if (!props.pendingDefId || readOnly) return;
    // A click that ended a pan or a drag was a gesture, not a placement.
    if (drag && drag.kind !== "pan") return;
    const world = toWorld(viewport, screenOf(event));
    props.onPlace?.({ defId: props.pendingDefId, atMm: snapForDisplay(world) });
  }, [drag, props, readOnly, screenOf, viewport]);

  const gridStep = gridStepForScale(viewport.scale);
  const sceneTransform = `translate(${-viewport.originMm.x * viewport.scale} ${-viewport.originMm.y * viewport.scale}) scale(${viewport.scale})`;

  const hoveredPortKey = hover.kind === "port" ? `${hover.symbol.id}:${hover.port.id}` : undefined;
  const hoveredTraceId = hover.kind === "trace" ? hover.trace.id : undefined;
  const hoveredSymbolId = hover.kind === "symbol" ? hover.symbol.id : undefined;

  const unresolvedIds = useMemo(() => new Set(projection.unresolved.map(entry => entry.id)), [projection.unresolved]);

  return (
    <div
      ref={surfaceRef}
      className="canvas-surface"
      data-testid="diagram-canvas"
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onClick={onClick}
    >
      <svg width={size.width} height={size.height} role="img" aria-label={`${projection.title} (${projection.diagramType})`}>
        <defs>
          <pattern id="mm-grid" width={gridStep * viewport.scale} height={gridStep * viewport.scale} patternUnits="userSpaceOnUse">
            <path d={`M ${gridStep * viewport.scale} 0 L 0 0 0 ${gridStep * viewport.scale}`} fill="none" stroke="var(--grid)" strokeWidth={0.5} />
          </pattern>
        </defs>

        <g transform={sceneTransform}>
          {/* The sheet, at its declared size in millimetres. */}
          <rect x={0} y={0} width={projection.sheet.widthMm} height={projection.sheet.heightMm} fill="var(--sheet)" stroke="var(--sheet-edge)" strokeWidth={0.5 / viewport.scale} />
        </g>
        <rect
          x={-viewport.originMm.x * viewport.scale} y={-viewport.originMm.y * viewport.scale}
          width={projection.sheet.widthMm * viewport.scale} height={projection.sheet.heightMm * viewport.scale}
          fill="url(#mm-grid)" pointerEvents="none"
        />

        <g transform={sceneTransform}>
          {projection.traces.map(trace => (
            <polyline
              key={trace.id}
              points={trace.path.map(point => `${point.x},${point.y}`).join(" ")}
              fill="none"
              stroke={trace.id === hoveredTraceId ? "var(--accent)" : traceColour(trace.traceDomain)}
              strokeWidth={trace.lineWidthMm}
              strokeLinecap="round"
              strokeLinejoin="round"
              data-trace-id={trace.id}
            />
          ))}

          {projection.symbols.map(symbol => (
            <g
              key={symbol.id}
              data-symbol-id={symbol.id}
              className={[
                "symbol",
                symbol.id === props.selectedId ? "is-selected" : "",
                symbol.id === hoveredSymbolId ? "is-hovered" : "",
                unresolvedIds.has(symbol.id) ? "is-unresolved" : "",
              ].filter(Boolean).join(" ")}
            >
              <g transform={instanceTransform(symbol)}>{symbol.geometry.map(renderPrimitive)}</g>
              {symbol.ports.map(port => (
                <circle
                  key={port.id}
                  cx={port.center.x} cy={port.center.y}
                  r={`${symbol.id}:${port.id}` === hoveredPortKey ? 1.6 : 1.0}
                  className={`port${port.connected ? " is-connected" : ""}`}
                  data-port-id={port.id}
                >
                  <title>{portTitle(port)}</title>
                </circle>
              ))}
              {symbol.id === props.selectedId && (
                <rect
                  x={symbol.bbox.x} y={symbol.bbox.y} width={symbol.bbox.w} height={symbol.bbox.h}
                  className="selection-box" fill="none" strokeWidth={0.4}
                />
              )}
            </g>
          ))}

          {projection.annotations.map(annotation => (
            <text key={annotation.id} x={annotation.position.x} y={annotation.position.y} className="annotation" fontSize={3}>
              {annotation.text ?? ""}
            </text>
          ))}

          {/* Ghost: where the Engine will be ASKED to put things, not where they will land. */}
          {drag?.kind === "connect" && drag.port && (
            <line
              x1={drag.port.boundary.x} y1={drag.port.boundary.y}
              x2={drag.currentWorld.x} y2={drag.currentWorld.y}
              className="ghost-connect" strokeWidth={0.35} strokeDasharray="2 1.5"
            />
          )}
          {drag?.kind === "move" && drag.symbol && (
            <rect
              x={snapForDisplay(drag.currentWorld).x - drag.symbol.bbox.w / 2}
              y={snapForDisplay(drag.currentWorld).y - drag.symbol.bbox.h / 2}
              width={drag.symbol.bbox.w} height={drag.symbol.bbox.h}
              className="ghost-move" fill="none" strokeWidth={0.4} strokeDasharray="2 1.5"
            />
          )}
          {props.pendingDefId && pointerWorld && !drag && (
            <g className="ghost-place" transform={`translate(${snapForDisplay(pointerWorld).x} ${snapForDisplay(pointerWorld).y})`}>
              <circle r={3} strokeDasharray="1.5 1" strokeWidth={0.35} fill="none" />
              <line x1={-5} y1={0} x2={5} y2={0} strokeWidth={0.35} />
              <line x1={0} y1={-5} x2={0} y2={5} strokeWidth={0.35} />
            </g>
          )}
        </g>
      </svg>

      <div className="canvas-readout">
        <span>{projection.sheet.format} · {projection.sheet.widthMm}×{projection.sheet.heightMm} mm</span>
        <span>{Math.round(viewport.scale * 100) / 100} px/mm</span>
        {pointerWorld && <span>{pointerWorld.x.toFixed(1)}, {pointerWorld.y.toFixed(1)} mm</span>}
        {props.pendingDefId && <span className="armed">placing {props.pendingDefId}</span>}
      </div>
    </div>
  );
}

function portTitle(port: ProjectedPort): string {
  const parts = [port.label ?? port.id, port.kindFamily];
  if (port.kindSubfamily) parts.push(port.kindSubfamily);
  // The semantic role, stated explicitly. Nothing in this UI infers flow from
  // which side of a symbol a port sits on.
  parts.push(port.flowDirection ? `flow: ${port.flowDirection}` : "flow: not declared");
  return parts.join(" · ");
}

function traceColour(domain: string): string {
  switch (domain) {
    case "process": return "var(--trace-process)";
    case "electrical": return "var(--trace-electrical)";
    case "signal": case "instrument": return "var(--trace-signal)";
    default: return "var(--trace-other)";
  }
}
